'use client';

import type { LocaleCode } from '@ifixit/i18n/types';
import { useStoreLocale } from '@ifixit/i18n/hooks';
import { useState, useEffect } from 'react';
import { StaticImage } from './static-asset';
import { cn } from '../lib/utils';

type AppStoreButtonsProps = {
   className?: string;
   buttonClassName?: string;
};
type MobileOS = 'ios' | 'android' | 'other';

export function AppStoreButtons({ className, buttonClassName }: AppStoreButtonsProps) {
   const [os, setOS] = useState<MobileOS>('other');
   const locale = useStoreLocale();

   useEffect(() => {
      setOS(detectMobileOS());
   }, []);

   const storesToShow: Array<Exclude<MobileOS, 'other'>> =
      os === 'other' ? ['ios', 'android'] : [os === 'ios' ? 'ios' : 'android'];

   const appleCountryCode = getAppleStoreCountryCode(locale);
   const appStoreUrl = `https://apps.apple.com/${appleCountryCode}/app/id6755199743`;

   // These different sizes make the visual height match
   const storeConfigs = {
      ios: {
         href: appStoreUrl,
         ariaLabel: 'Download on the App Store',
         image: 'images/app-landing/download-on-the-app-store-apple-logo-svgrepo-com.svg',
         alt: 'Download on the App Store',
      },
      android: {
         href: 'https://play.google.com/store/apps/details?id=com.ifixit.ifixitmobile',
         ariaLabel: 'Get it on Google Play',
         image: 'images/app-landing/GetItOnGooglePlay_Badge_Web_color_English-01_fixed.svg',
         alt: 'Get it on Google Play',
      },
   } as const;

   return (
      <div className={cn('flex flex-col items-center justify-center gap-4 sm:flex-row', className)}>
         {storesToShow.map(store => {
            const config = storeConfigs[store];
            return (
               <a
                  key={store}
                  href={config.href}
                  className={cn(
                     'group transition-all duration-300 hover:scale-105 active:scale-95 h-16',
                     buttonClassName
                  )}
                  aria-label={config.ariaLabel}
               >
                  <StaticImage
                     path={config.image}
                     alt={config.alt}
                     className="transition-all duration-300 drop-shadow group-hover:brightness-110 group-hover:drop-shadow-lg h-full w-auto"
                  />
               </a>
            );
         })}
      </div>
   );
}

function detectMobileOS(): MobileOS {
   if (typeof window === 'undefined') return 'other';

   const userAgent = window.navigator.userAgent.toLowerCase();

   // Check for iOS
   if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
   }

   // Check for Android
   if (/android/.test(userAgent)) {
      return 'android';
   }

   return 'other';
}

/**
 * Maps iFixit locales to Apple App Store country codes.
 * Falls back to 'us' for unsupported countries.
 * For English EU locale (en-eu), uses UK store (gb) as Apple's standard English locale for EU.
 */
function getAppleStoreCountryCode(locale: LocaleCode): string {
   if (locale === 'en-eu') {
      return 'gb';
   }
   return locale.slice(3);
}
